import React from 'react';
import { useParams } from 'react-router';
import { TextVariant } from '@naf/text';
import { Marker } from '@loadable-elements/LoadableGoogleMapsAPI';
import { NotFound } from '@loadable-elements/NotFound';
import { Directions } from '@naf-simba/building-blocks-icons/Travel';
import { PracticeTrackType } from '../../../../../../types/PracticeTrackType';
import { useSendGTMEventOnce } from '../../../../hooks/GTM/useSendGTMEventOnce';
import { LoaderContent } from '../../../LoaderPage';
import { useWindowLocation } from '../../../../hooks/Window/useWindowLocation';
import BlockContent from '../../../../components/block-content/BlockContent';
import carIcon from '../../../../../assets/images/carPOI.svg?url';
import { useFetchCity } from '../../../../hooks/useFetchCity';
import { OneLineLoader } from '../../../../components/skeleton-loaders/OneLineLoader';
import { useHandleNafLocationsRoutes } from '../../useHandleNafLocationsRoutes';
import { useLocationDocument } from '../../useLocationDocument';
import { LayoutWithMainContent } from '../../../../components/layout/Layout';
import { InternalSearchCardType } from '../../../../components/layout/InternalSearchMetaData';
import * as S from './PracticeTrack.styles';

const PracticeTrack = () => {
  const { slug } = useParams<{ slug: string }>();
  const { document, meta } = useLocationDocument<PracticeTrackType>(slug);
  const url = useWindowLocation();
  const { locations } = useHandleNafLocationsRoutes();

  useSendGTMEventOnce(
    document && {
      page_type: 'nafLocationsApp',
      pageCategory: `Her finner du NAF / ${Object.values(locations).find((l) => url.includes(l.fullUrl))?.name} / ${
        document.title
      }`,
      contentId: document.id,
    },
    [document],
  );
  const { cityName } = useFetchCity(document?.contactInfo?.postalCode);

  if (!document && meta.isUpdating) {
    return <LoaderContent />;
  }
  if (!document) {
    return <NotFound />;
  }
  const { title, ingress, coordinates, seoConfig, body, contactInfo } = document;
  const hasAddress = Boolean(contactInfo?.streetAddress || contactInfo?.postalCode);
  const hasContact = Boolean(contactInfo?.contactPersonName || contactInfo?.phoneNumber || contactInfo?.email);

  return (
    <LayoutWithMainContent
      title={title}
      description={seoConfig?.introduction || ingress}
      seoDescription={seoConfig?.introduction || undefined}
      isHiddenFromSearch={seoConfig?.isHiddenFromSearch}
      seoTitle={seoConfig?.title || title}
      internalSearchMetaData={{
        cardType: InternalSearchCardType.PracticeTrack,
        streetAddress: contactInfo?.streetAddress,
        postalCode: contactInfo?.postalCode,
        contactPhone: contactInfo?.phoneNumber,
        contactEMail: contactInfo?.email,
      }}
      url={url}
    >
      <S.StyledBreadcrumb />
      <S.StyledGrid s="6">
        <S.Header variant={TextVariant.Header1}>{title}</S.Header>
        <S.Ingress variant={TextVariant.Ingress}>{ingress}</S.Ingress>
        {coordinates && (
          <S.Map>
            <S.StyledMapWrapper
              mapOptions={{
                mapId: '241506239fc98522',
                disableDefaultUI: true,
              }}
              center={{ lat: coordinates.latitude, lng: coordinates.longitude }}
              zoom={9}
            >
              <Marker position={{ lat: coordinates.latitude, lng: coordinates.longitude }} icon={{ url: carIcon }} />
              <S.Mapbutton
                startIcon={Directions}
                color="secondary"
                href={`https://www.google.com/maps/dir/?api=1&destination=${coordinates.latitude},${coordinates.longitude}&travelmode=driving`}
                target="_blank"
              >
                Få veibeskrivelse
              </S.Mapbutton>
            </S.StyledMapWrapper>
          </S.Map>
        )}
        <S.Body>
          <BlockContent value={body} />
        </S.Body>
        <S.Contact>
          {hasAddress && (
            <div>
              <S.ContactHeader variant={TextVariant.BodyTextHeader}>Besøksadresse</S.ContactHeader>
              {contactInfo?.streetAddress && (
                <S.NoMarginText variant={TextVariant.BodyText}>{contactInfo.streetAddress}</S.NoMarginText>
              )}
              {contactInfo?.postalCode && (
                <S.NoMarginText variant={TextVariant.BodyText}>
                  {contactInfo.postalCode} {cityName || <OneLineLoader width="32px" height="16px" />}
                </S.NoMarginText>
              )}
            </div>
          )}
          {hasContact && (
            <div>
              <S.ContactHeader variant={TextVariant.BodyTextHeader}>Kontakt</S.ContactHeader>
              {contactInfo?.contactPersonName && (
                <S.NoMarginText variant={TextVariant.BodyText}>{contactInfo.contactPersonName}</S.NoMarginText>
              )}
              {contactInfo?.phoneNumber && (
                <S.NoMarginText variant={TextVariant.BodyText}>
                  Tlf: <a href={`tel:${contactInfo.phoneNumber}`}>{contactInfo.phoneNumber}</a>
                </S.NoMarginText>
              )}
              {contactInfo?.email && (
                <S.NoMarginText variant={TextVariant.BodyText}>
                  E-post: <a href={`mailto:${contactInfo.email}`}>{contactInfo.email}</a>
                </S.NoMarginText>
              )}
            </div>
          )}
        </S.Contact>
      </S.StyledGrid>
    </LayoutWithMainContent>
  );
};

export default PracticeTrack;
